import type { KboSearchRow } from "../../../api/company";
import type { Company } from "../../../types/companyTypes";

/**
 * Which companies the Customer Switch destination picker may offer, from TWO
 * sources, as a UNION — never a replacement.
 *
 *   Section 1  companies the caller is already a contact of (`allCompany` redux,
 *              read from `company_contact`).
 *   Section 2  rows from the Belgian enterprise register (`kbo`) that are NOT yet
 *              in our `companies` table, and can therefore be onboarded inline.
 *
 * 🔴 IT MUST BE A UNION. The two sources have different populations: a company we hold
 * that has no register row would vanish if section 2 replaced section 1 — and 221 of
 * the 5 077 companies we hold have no `kbo` row at all (foreign VATs: LU/NL/EE). As a
 * union, a register that returns nothing degrades the picker to exactly the behaviour
 * it had before section 2 existed.
 *
 * The register is LOADED on dev: 1 952 120 rows, 1 621 754 of them (83%) carrying a
 * leading zero. It is not empty, and section 2 is not dead weight — an earlier version
 * of this comment said the opposite and was wrong.
 *
 * Kept pure and rendering-free on the `switchGroup.ts` precedent, so the merge rules
 * can be unit-tested without a DOM.
 */

/** Rendered cap for the LINKED list. An admin can be a contact of thousands. */
export const MAX_RESULTS = 50;

/**
 * What `GET /api/v1/company/search/:term` returns at most, per
 * `yuso-auth-api/src/services/kboDbService.ts`. That file is the ONLY place this can
 * change; there is no total in the response, so the FE cannot compute one.
 */
export const KBO_SERVER_LIMIT = 15;

export interface RegisterCandidate {
  /**
   * IDENTITY ONLY. `companies.id === companies.companyNumber === kbo.number`, all
   * `bigint`/`int unsigned`, so this is the correct key to dedupe against `Company.id`
   * and the correct value to send as the create payload's `kbo_number`.
   *
   * 🔴 NEVER RENDER THIS. See `displayNumber`.
   */
  number: number;
  /**
   * The enterprise number AS PRINTED — what the user must see.
   *
   * `kbo.number` is `int unsigned` and cannot hold a leading zero, yet 1 621 754 of the
   * 1 952 120 register rows on dev (83%) have one. Showing `number` therefore shows most
   * users a number that is not theirs: `200065765` where their paperwork says
   * `0200065765`. `formattedNumber` ("0200.065.765") is preferred, `numberText`
   * ("0200065765") is the fallback, and `vatNumber` ("BE 0200.065.765") is the last
   * resort — all three carry the zero; only `number` loses it.
   */
  displayNumber: string;
  row: KboSearchRow;
}

/**
 * Pick the display form for one register row, in preference order. Returns "" only if
 * the row carries none of the three zero-preserving forms — in which case the caller
 * shows nothing rather than showing a wrong number.
 *
 * Verified against the full dev register (1 952 120 rows): `vatNumber` is
 * `BE 0000.000.000` and `formattedNumber` is `0000.000.000` for 100% of rows, and
 * `numberText` is exactly `LPAD(number, 10, '0')` with 0 mismatches.
 */
export const registerDisplayNumber = (row: KboSearchRow): string =>
  (row.metadata?.formattedNumber || row.metadata?.numberText || row.vatNumber || "").trim();

export interface RegisterCandidates {
  candidates: RegisterCandidate[];
  /**
   * How many rows the SERVER returned, BEFORE dedupe. The "showing N" hint keys on
   * this: keying it on the rendered count would hide the hint exactly when the list
   * is most truncated (many results, most of them already ours).
   */
  rawCount: number;
}

/**
 * The full pool of linked companies, minus company A. A can never be its own
 * destination.
 *
 * 🔴 UNCAPPED ON PURPOSE. This is what `registerCandidates` dedupes against. Deduping
 * against the RENDERED 50 instead would let a linked company sitting past the cap
 * reappear in section 2 as "not with us", and the admin would then try to create a
 * company they are already on — a self-inflicted 409, on a screen whose whole job is
 * to be certain about membership.
 */
export const linkedPool = (companies: Company[], sourceCompanyId: number): Company[] =>
  companies.filter((c) => c.id !== sourceCompanyId);

/** Section 1's rendered list: the same match rules the picker has always used. */
export const matchLinked = (pool: Company[], term: string, max: number = MAX_RESULTS): Company[] => {
  const q = term.trim().toLowerCase();
  if (q === "") return pool.slice(0, max);
  return pool
    .filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        String(c.id).startsWith(q) ||
        (c.reg_number ?? "").toLowerCase().includes(q)
    )
    .slice(0, max);
};

/**
 * 🔴 THE VISIBILITY RULE, and it is a product decision, not an optimisation.
 *
 * Keep a register row iff `existsInCompanies === false` OR `linkedToCaller === true`.
 *
 * The row it excludes is the interesting one: a company that IS in `companies` but
 * is NOT linked to the caller. Those are hidden from BOTH sections — they are not
 * offerable as a destination (the switch's own guard would refuse) and they are not
 * creatable (they already exist). Decided by JediMaster; not to be re-litigated.
 *
 * Note the strict `=== false` / `=== true`: a frontend deployed ahead of the backend
 * sees both flags as `undefined`, and every such row is dropped. That is the SAFE
 * direction — the register section renders empty and the picker behaves exactly as
 * it does today — rather than offering creates that would all 409.
 *
 * ⚠️ SUBSUMED TODAY, AND NO TEST CAN PIN THE CALL SITE. `registerCandidates` also
 * requires `existsInCompanies === false`, which is STRICTLY STRONGER than this OR —
 * every row that survives that requirement satisfies this predicate automatically.
 * Deleting the call below therefore changes NOTHING observable, and no assertion
 * driven through `registerCandidates` can go red on it. Verified by mutation.
 *
 * It is kept as the second of two independent halves — the same pattern documented at
 * `CustomerSwitchTransfer.tsx:195-219` — because it is the half that survives a future
 * WEAKENING of the `existsInCompanies` requirement. What IS pinned, and what does fail
 * on that weakening, is the subsumption invariant in
 * `tests/utils/destinationCandidates.test.ts`: every row that yields a candidate must
 * satisfy `keepForPicker`. If you take one half out you are relying entirely on the
 * other: do that deliberately, or not at all.
 */
export const keepForPicker = (row: KboSearchRow): boolean =>
  row.existsInCompanies === false || row.linkedToCaller === true;

/**
 * VAT, reduced to comparable characters. `companies.vatCode` is `BE 0848.296.771` and
 * `kbo.vatNumber` is `BE 0782.820.286` — same shape, but nothing enforces the spacing.
 */
const normalizeVat = (vat: string | null | undefined): string =>
  (vat ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * Section 2: register rows that can actually be onboarded from here.
 *
 * Beyond `keepForPicker`, a candidate must also NOT already exist in `companies`.
 * The rows that pass the OR rule via `linkedToCaller === true` are ALREADY OURS and
 * belong to section 1 — offering them here would mean offering a create for a
 * company that exists. Normally the dedupe below removes them anyway, but the two
 * facts come from two different endpoints: if `allCompany` is stale, empty, or its
 * fetch rejected, the dedupe alone would let such a row through. Requiring
 * `existsInCompanies === false` closes that independently of the redux slice.
 */
export const registerCandidates = ({
  rows,
  pool,
  sourceCompanyNumber,
}: {
  rows: KboSearchRow[];
  pool: Company[];
  /**
   * 🔴 `companyNumber`, NOT the `company_id` PK — a DIFFERENT ID SPACE.
   *
   * `kbo.number` is what `companies.companyNumber` is joined on, so that is the only
   * space a register row's number can be compared in. The base branch's own tip commit
   * `c44188b` — "compare provenance against companyNumber, not the PK" — fixed exactly
   * this class one file away; comparing against the PK works only while the two columns
   * hold equal values (true for all 5 077 companies today, enforced by nothing).
   */
  sourceCompanyNumber: number;
}): RegisterCandidates => {
  /**
   * Dedupe key set from the FULL pool — see `linkedPool`.
   *
   * ⚠️ `Company.id` IS THE PK, AND THAT IS A CROSS-SPACE COMPARISON I CANNOT CLOSE HERE.
   * `getCompaniesByContactEmail` (`yuso-portals-backend/src/functions/companies.ts:133`)
   * selects `c.id` and `c.vatCode AS reg_number` and NO `companyNumber` at all, so the
   * payload simply does not carry the value this should be compared against. Closing it
   * properly needs `c.companyNumber AS company_number` added to that SELECT — a
   * portals-backend change, out of scope here.
   *
   * Mitigated rather than ignored: the VAT set below is a SECOND, id-space-independent
   * key. A register row is dropped if EITHER matches, so the dedupe survives an
   * id/companyNumber divergence that would defeat the numeric key alone.
   */
  const knownNumbers = new Set<number>(
    pool.map((c) => Number(c.id)).filter((n) => Number.isFinite(n))
  );
  /**
   * The VAT key. Measured on dev: `companies.vatCode` equals `kbo.vatNumber` for 4 849
   * of the 4 856 companies that join to a register row, so this catches essentially
   * every case the numeric key does — and does so without touching either id space.
   *
   * It can only ever ADD a dedupe hit, i.e. hide a row that is already ours. That is the
   * safe direction: a false positive would need two DIFFERENT companies to share one VAT
   * string. The 7 disagreements and the 2 blank vatCodes are why it SUPPLEMENTS the
   * numeric key instead of replacing it.
   */
  const knownVats = new Set<string>(
    pool.map((c) => normalizeVat(c.reg_number)).filter((v) => v.length > 0)
  );

  const candidates: RegisterCandidate[] = [];
  const seen = new Set<number>();

  for (const row of rows) {
    if (!keepForPicker(row)) continue;
    if (row.existsInCompanies !== false) continue;

    const number = Number(row.metadata?.number);
    // A row we cannot identify cannot be deduped, cannot be created, and cannot be
    // selected afterwards — `company_id` in the create response IS this number.
    if (!Number.isFinite(number)) continue;
    /**
     * Company A can never be its own destination.
     *
     * ⚠️ DEFENCE IN DEPTH, currently UNREACHABLE through the real backend: company A is
     * by definition in `companies`, so its register row comes back with
     * `existsInCompanies === true` and the check above has already dropped it. The guard
     * is kept because it states an invariant that does not depend on that reasoning
     * staying true, and it is pinned directly at the function's contract with a
     * synthetic row — which is honest about what it proves and what it does not.
     */
    if (number === Number(sourceCompanyNumber)) continue;
    if (knownNumbers.has(number)) continue;
    const vat = normalizeVat(row.vatNumber);
    if (vat.length > 0 && knownVats.has(vat)) continue;
    if (seen.has(number)) continue;

    seen.add(number);
    candidates.push({ number, displayNumber: registerDisplayNumber(row), row });
  }

  return { candidates, rawCount: rows.length };
};

/**
 * The company the picker selects once an inline create has committed.
 *
 * `company_id` from the 201/409 body is AUTHORITATIVE — it is the KBO number, and
 * therefore also `companies.id`, `companies.companyNumber` and
 * `billing_information.billing_id`. Nothing is minted client-side.
 *
 * Two mismatches with what the backend actually stored, recorded rather than papered
 * over. Both are COSMETIC — traced through: `destination.name` reaches
 * `modalProps.companyName`, which `CustomerSwitchModal` passes to
 * `InjectionContractParameters`/`OfftakeContractParameters` as
 * `selectedUserCompany={{ companyName }}`, where it is rendered as heading TEXT
 * (`InjectionContractParameters.tsx:117`, `OfftakeContractParameters.tsx:110`) and
 * never enters a request payload or a generated document.
 *
 * 🔴 THEY DO NOT SELF-HEAL. An earlier version of this comment claimed they "resolve
 * on the next `fetchCompanies`". They do not: `destination` is local state in
 * `CustomerSwitchTransfer` and is never re-derived from `allCompany`, so the
 * synthesized values persist for the life of the selection. The refetch updates the
 * LIST, not the selection made from it.
 *   - the backend stores `companyName = "<legal name> <legalFormShort>"`, so the
 *     name shown between the create and the refetch is the legal name alone;
 *   - `Company.address_zip` is typed `number` while the draft holds a string.
 */
export const companyFromCreated = ({
  companyId,
  name,
  regNumber,
  addressStreet,
  addressNumber,
  addressZip,
  addressCity,
  addressCountry,
}: {
  companyId: number;
  name: string;
  /**
   * 🔴 The ZERO-PADDED display form, never `String(companyId)`.
   *
   * `getAllUserCompanies` returns `reg_number` as the 10-digit zero-padded number
   * ("0405316874") for every other company, and this row is about to sit alongside them
   * once `fetchCompanies` lands. Passing the int form would make the freshly created
   * company render with a different — and for 83% of companies, wrong — number for the
   * seconds before the refetch replaces it.
   */
  regNumber: string;
  addressStreet: string;
  addressNumber: string;
  addressZip: string;
  addressCity: string;
  addressCountry: string;
}): Company => ({
  id: companyId,
  reg_number: regNumber,
  name,
  role_type: "",
  address_street: addressStreet,
  address_number: addressNumber,
  address_zip: Number(addressZip) || 0,
  address_city: addressCity,
  address_country: addressCountry,
});
